import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom, lastValueFrom, Subject } from 'rxjs';
import { SocketService } from 'src/app/core/services';
import { environment } from 'src/environments/environment';

/**
 * GroupeService
 * @class GroupeService
 */
@Injectable()
export class GroupeService {
  /**
   * Injected HttpClient instance for making HTTP requests
   * @type {HttpClient}
   */
  private http = inject(HttpClient);
  private echoInstance = null;

  private socketGroup$: Subject<any> = new Subject();
  private socketService = inject(SocketService);

  /**
   * Base URL for group management API
   * @type {string}
   */
  private baseApiUrl = environment.apiUrl + 'company/group/manage';

  /**
   * Base URL for group details API
   * @type {string}
   */
  private baseApiUrlDetails = environment.apiUrl + 'company/group/manage/details';
  /**
   * open the socket for group and listen to event CheckLockedGroupEvent
   */
  startSocket() {
    this.echoInstance = this.socketService.getEchoInstance();
    this.echoInstance.private(`company.${this.socketService.companyId}.group`)
      .listen('.CheckLockedGroupEvent', (event) => {
        this.socketGroup$.next(event.group);
      })
  }
  /**
   * subscribe to Observable
   * @returns {Observable<any>}
   */
  subSocketGroup() {
    return this.socketGroup$;
  }
  /**
   * stop the socket for group when needed
   */

  stopSocket() {
    if (this.echoInstance) {
      this.echoInstance.leave(`company.${this.socketService.companyId}.group`);
    }
  }
  /**
   * Retrieves the list of groups with the specified filters.
   *
   * @param {any} filters
   * @returns {Promise<any>}
   */
  getGroups(filters): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl, { params: filters }));
  }

  /**
   * Adds a new group
   *
   * @param {any} data
   * @returns {Promise<any>}
   */
  addGroup(data): Promise<any> {
    return firstValueFrom(this.http.post(this.baseApiUrl, data));
  }

  /**
   * Deletes an existing group
   *
   * @param {number} id
   * @param {any} data
   * @returns {Promise<any>}
   */
  deleteGroup(id, data): Promise<any> {
    return firstValueFrom(this.http.request('delete', `${this.baseApiUrl}/${id}`, { body: data }));
  }

  /**
   * Updates an existing group
   *
   * @param {number} id
   * @param {any} data
   * @returns {Promise<any>}
   */
  updateGroup(id, data): Promise<any> {
    return firstValueFrom(this.http.put(`${this.baseApiUrl}/${id}`, data));
  }

  /**
     * Retrieves details for a group/groups
     * If no ID is provided, retrieves details for all groups
     *
     * @param {any} [id] - optional ID
     * @returns {Promise<any>}
     */
  async getGroupDetails(id?: any): Promise<any> {
    try {
      const headers = new HttpHeaders().set("accept", "text/event-stream");
      const response = await lastValueFrom(this.http.get(`${this.baseApiUrlDetails}${id ? `/${id}` : ''}`, { headers, responseType: "text" }));
      return response;
    } catch (error) {
      throw error;
    }
  }
  /**
   * Retrieves users of group/groups
   *
   * If no ID is provided, retrieves users details for all groups
   *
   * @param {any} [id] - optional ID
   * @returns {Promise<any>}
   */
  async getGroupDetailsUser(id?: any): Promise<any> {
    try {
      const headers = new HttpHeaders().set("accept", "text/event-stream");
      const response = await lastValueFrom(this.http.get(`${this.baseApiUrlDetails}/${id ? `${id}/` : ''}users`, { headers, responseType: "text" }));
      return response;
    } catch (error) {
      throw error;
    }
  }
  /**
    * Retrieves preloadPasswords of group/groups
    *
    * If no ID is provided, retrieves preloadPasswords for all groups
    *
    * @param {any} [id] - optional ID
    * @returns {Promise<any>}
    */
  async getGroupDetailsPreloadPassword(id?: any): Promise<any> {
    try {
      const headers = new HttpHeaders().set("accept", "text/event-stream");
      const response = await lastValueFrom(this.http.get(`${this.baseApiUrlDetails}/${id ? `${id}/` : ''}preloadpassword`, { headers, responseType: "text" }));
      return response;
    } catch (error) {
      throw error;
    }
  }
  /**
   * Retrieves credit cards of a group/groups
   * If no ID is provided, retrieves credit cards for all groups.
   *
   * @param {any} [id] - optional ID
   * @returns {Promise<any>}
   */
  async getGroupDetailsCreditCard(id?: any): Promise<any> {
    try {
      const headers = new HttpHeaders().set("accept", "text/event-stream");
      const response = await lastValueFrom(this.http.get(`${this.baseApiUrlDetails}/${id ? `${id}/` : ''}creditcard`, { headers, responseType: "text" }));
      return response;
    } catch (error) {
      throw error;
    }
  }
  /**
  * Retrieves preloadURLs of a group/groups
  * If no ID is provided, retrieves preload URLs for all groups.
  * @param {any} [id] - optional ID
  * @returns {Promise<any>}
  */
  async getGroupDetailsPreloadUrl(id?: any): Promise<any> {
    try {
      const headers = new HttpHeaders().set("accept", "text/event-stream");
      const response = await lastValueFrom(this.http.get(`${this.baseApiUrlDetails}/${id ? `${id}/` : ''}preloadurl`, { headers, responseType: "text" }));
      return response;
    } catch (error) {
      throw error;
    }
  }

}
